import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { forwardRef } from 'react';
import { useTheme } from '@swift-paypal/pp-react-theme';
import clsx from 'clsx';
import { CriticalAltIcon } from '@swift-paypal/pp-react-icons';
import '../__generated__/styles/switch.css';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
function generateId(prefix) {
  return "".concat(prefix, "_").concat(Math.random().toFixed(6).toString().replace('0.', ''));
}
var IfNotEmpty = function (_ref) {
  var _ref$as = _ref.as,
      as = _ref$as === void 0 ? 'span' : _ref$as,
      children = _ref.children,
      className = _ref.className,
      rest = _objectWithoutProperties(_ref, ["as", "children", "className"]);

  return children ? React.createElement(as, _objectSpread({
    className: className
  }, rest), children) : null;
};

var styles = {"ppvx_switch":"ppvx_switch___2-9-1","ppvx_switch--inline":"ppvx_switch--inline___2-9-1","ppvx_switch__label":"ppvx_switch__label___2-9-1","ppvx_switch__helper-text":"ppvx_switch__helper-text___2-9-1","ppvx_switch__error-text":"ppvx_switch__error-text___2-9-1","ppvx_switch__state--off":"ppvx_switch__state--off___2-9-1","ppvx_switch__control":"ppvx_switch__control___2-9-1","ppvx_switch__state":"ppvx_switch__state___2-9-1","ppvx_switch__state--on":"ppvx_switch__state--on___2-9-1","ppvx_icon":"ppvx_icon___2-9-1","ppvx_switch__label-icon":"ppvx_switch__label-icon___2-9-1","ppvx_switch__label-secondary":"ppvx_switch__label-secondary___2-9-1","ppvx_field--error":"ppvx_field--error___2-9-1","ppvx_switch__icon--left":"ppvx_switch__icon--left___2-9-1","ppvx_switch__error-icon":"ppvx_switch__error-icon___2-9-1","ppvx_switch__error-text--with-svg-icon":"ppvx_switch__error-text--with-svg-icon___2-9-1","ppvx--v2":"ppvx--v2___2-9-1","ppvx--v1":"ppvx--v1___2-9-1"};

var SwitchIcon = function (_ref) {
  var icon = _ref.icon;

  if (!icon) {
    return null;
  }

  return React.cloneElement(icon, {
    className: clsx(icon.className, styles['ppvx_switch__icon--left'])
  });
}; // Get either the error message or helper text or... nothing


var renderMessage = function (err, help, msgId) {
  return err && /*#__PURE__*/React.createElement("div", {
    className: clsx(styles['ppvx_switch__error-text'], styles['ppvx_switch__error-text--with-svg-icon']),
    id: msgId
  }, /*#__PURE__*/React.createElement(CriticalAltIcon, {
    className: styles['ppvx_switch__error-icon'],
    size: "xs"
  }), err) || help && /*#__PURE__*/React.createElement("div", {
    className: styles['ppvx_switch__helper-text'],
    id: msgId
  }, help) || null;
};

var Switch = forwardRef(function (_ref2, ref) {
  var _clsx;

  var checked = _ref2.checked,
      inline = _ref2.inline,
      className = _ref2.className,
      inputClassName = _ref2.inputClassName,
      labelClassName = _ref2.labelClassName,
      errorText = _ref2.errorText,
      helperText = _ref2.helperText,
      hideLabel = _ref2.hideLabel,
      _ref2$id = _ref2.id,
      id = _ref2$id === void 0 ? generateId('Switch') : _ref2$id,
      label = _ref2.label,
      secondaryLabel = _ref2.secondaryLabel,
      labelIcon = _ref2.labelIcon,
      leftIcon = _ref2.leftIcon,
      onChange = _ref2.onChange,
      _ref2$onLabel = _ref2.onLabel,
      onLabel = _ref2$onLabel === void 0 ? 'On' : _ref2$onLabel,
      _ref2$offLabel = _ref2.offLabel,
      offLabel = _ref2$offLabel === void 0 ? 'Off' : _ref2$offLabel,
      required = _ref2.required,
      value = _ref2.value,
      dir = _ref2.dir,
      theme = _ref2.theme,
      rest = _objectWithoutProperties(_ref2, ["checked", "inline", "className", "inputClassName", "labelClassName", "errorText", "helperText", "hideLabel", "id", "label", "secondaryLabel", "labelIcon", "leftIcon", "onChange", "onLabel", "offLabel", "required", "value", "dir", "theme"]);

  var labelId = "label_".concat(id);
  var switchLabelId = "switch_label_".concat(id);
  var messageId = "message_".concat(id);

  var _theme = useTheme('Switch', theme);

  var isThemeV2 = _theme && _theme === 'v2';
  var containerClass = clsx(styles.ppvx_switch, (_clsx = {}, _defineProperty(_clsx, styles['ppvx_switch--inline'], inline), _defineProperty(_clsx, styles['ppvx_field--error'], !!errorText && !isThemeV2), _defineProperty(_clsx, styles['ppvx--v2'], isThemeV2), _clsx), className);
  var inputClasses = clsx(styles.ppvx_switch__control, inputClassName);
  var labelClasses = clsx(styles.ppvx_switch__state, labelClassName);
  var valueProps = onChange ? {
    onChange: onChange,
    checked: checked
  } : {
    defaultChecked: checked
  };
  var labelIconElm = labelIcon && /*#__PURE__*/React.createElement("span", {
    className: styles['ppvx_switch__label-icon']
  }, labelIcon);
  return /*#__PURE__*/React.createElement("div", {
    className: containerClass,
    dir: dir
  }, !hideLabel && /*#__PURE__*/React.createElement("label", {
    id: labelId,
    htmlFor: id,
    className: styles.ppvx_switch__label
  }, label, labelIconElm, /*#__PURE__*/React.createElement(IfNotEmpty, {
    as: "div",
    className: styles['ppvx_switch__label-secondary']
  }, secondaryLabel)), /*#__PURE__*/React.createElement(SwitchIcon, {
    icon: leftIcon
  }), /*#__PURE__*/React.createElement("input", _extends({
    "aria-describedby": messageId //@ts-ignore
    ,
    "aria-label": hideLabel ? label : null,
    className: inputClasses,
    id: id,
    role: "switch",
    type: "checkbox",
    value: value,
    ref: ref
  }, valueProps, rest)), /*#__PURE__*/React.createElement("label", {
    className: labelClasses,
    id: switchLabelId,
    htmlFor: id
  }, /*#__PURE__*/React.createElement("span", {
    className: styles['ppvx_switch__state--on']
  }, onLabel), /*#__PURE__*/React.createElement("span", {
    className: styles['ppvx_switch__state--off']
  }, offLabel)), renderMessage(!isThemeV2 && errorText, helperText, messageId));
});

export { Switch };
